import { useState, useEffect, useMemo } from 'react'
import { cohortsAPI, campaignsAPI } from '../../services/api'
import { getAvatarData } from '../../utils/avatarUtils'

/**
 * Icono de delivery status estilo WhatsApp.
 * - failed: ✗ rojo (con tooltip de razón)
 * - read: ✓✓ azul
 * - delivered: ✓✓ gris
 * - sent: ✓ gris (default)
 */
function StatusIcon({ log }) {
  if (log.failed_at || log.status === 'failed') {
    return (
      <span className="text-red-500 text-base shrink-0" title={`Falló: ${log.fail_reason || 'sin detalle'}`}>✗</span>
    )
  }
  if (log.read_at) {
    return (
      <svg className="w-4 h-4 text-blue-500 shrink-0" viewBox="0 0 16 11" fill="none" title={`Leído ${new Date(log.read_at).toLocaleString('es-CL')}`}>
        <path d="M11.071.653a.5.5 0 0 1 .222.667L6.222 11.5a.5.5 0 0 1-.875.054L.222 4.873a.5.5 0 0 1 .778-.625l4.404 5.5L10.404.875a.5.5 0 0 1 .667-.222z" fill="currentColor" />
        <path d="M15.071.653a.5.5 0 0 1 .222.667L10.222 11.5a.5.5 0 0 1-.875.054l-1.5-2 .778-.625 1.404 1.875L14.404.875a.5.5 0 0 1 .667-.222z" fill="currentColor" />
      </svg>
    )
  }
  if (log.delivered_at) {
    return (
      <svg className="w-4 h-4 text-gray-400 shrink-0" viewBox="0 0 16 11" fill="none" title={`Entregado ${new Date(log.delivered_at).toLocaleString('es-CL')}`}>
        <path d="M11.071.653a.5.5 0 0 1 .222.667L6.222 11.5a.5.5 0 0 1-.875.054L.222 4.873a.5.5 0 0 1 .778-.625l4.404 5.5L10.404.875a.5.5 0 0 1 .667-.222z" fill="currentColor" />
        <path d="M15.071.653a.5.5 0 0 1 .222.667L10.222 11.5a.5.5 0 0 1-.875.054l-1.5-2 .778-.625 1.404 1.875L14.404.875a.5.5 0 0 1 .667-.222z" fill="currentColor" />
      </svg>
    )
  }
  // sent (default)
  return (
    <svg className="w-3 h-3 text-gray-400 shrink-0" viewBox="0 0 11 11" fill="none" title="Enviado">
      <path d="M9.071.653a.5.5 0 0 1 .222.667L4.222 11.5a.5.5 0 0 1-.875.054L.222 6.873a.5.5 0 0 1 .778-.625l2.404 3 5-6.373a.5.5 0 0 1 .667-.222z" fill="currentColor" />
    </svg>
  )
}

/**
 * Vista historial de pasos enviados.
 * Agrupa por día y dentro del día por campaña madre.
 * Cada envío muestra avatar circular del prospecto (mismo de TodoList).
 */
export default function StepHistoryView({ isAdmin }) {
  const [logs, setLogs] = useState([])
  const [campaigns, setCampaigns] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [range, setRange] = useState('7d') // 24h | 7d | 30d
  const [campaignFilter, setCampaignFilter] = useState('')
  const [onlyMine, setOnlyMine] = useState(!isAdmin)

  useEffect(() => {
    campaignsAPI.getCampaigns().then(d => setCampaigns(d.campaigns || [])).catch(() => {})
  }, [])

  const fetchLogs = async () => {
    try {
      setError(null)
      const now = new Date()
      const fromDate = new Date(now)
      if (range === '24h') fromDate.setDate(now.getDate() - 1)
      else if (range === '30d') fromDate.setDate(now.getDate() - 30)
      else fromDate.setDate(now.getDate() - 7)
      const r = await cohortsAPI.getStepLogs({
        from: fromDate.toISOString(),
        to: now.toISOString(),
        campaignId: campaignFilter || null,
        onlyMine,
        limit: 500,
      })
      setLogs(r.logs || [])
    } catch (e) {
      setError(e.response?.data?.error || e.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setLoading(true)
    fetchLogs()
  }, [range, campaignFilter, onlyMine])

  // Agrupar: día -> campaña -> [logs]
  const groupedByDayCampaign = useMemo(() => {
    const days = {}
    for (const log of logs) {
      const d = new Date(log.sent_at)
      const dStr = d.toLocaleDateString('en-CA', { timeZone: 'America/Santiago' })
      if (!days[dStr]) days[dStr] = {}
      const camp = days[dStr][log.campaign_id] || { name: log.campaign_name, logs: [] }
      camp.logs.push(log)
      days[dStr][log.campaign_id] = camp
    }
    return days
  }, [logs])

  const dayKeys = useMemo(() => Object.keys(groupedByDayCampaign).sort((a, b) => b.localeCompare(a)), [groupedByDayCampaign])

  const formatTime = (iso) => new Date(iso).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Santiago' })
  const formatDayLabel = (dStr) => {
    const [y, m, d] = dStr.split('-')
    const date = new Date(parseInt(y), parseInt(m) - 1, parseInt(d))
    const today = new Date()
    const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1)
    if (date.toDateString() === today.toDateString()) return 'Hoy'
    if (date.toDateString() === yesterday.toDateString()) return 'Ayer'
    return date.toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
  }

  if (loading) return <div className="p-6 text-gray-500">Cargando historial…</div>
  if (error) return <div className="p-6 text-red-600">Error: {error}</div>

  return (
    <div className="flex-1 p-6 overflow-y-auto bg-gray-50">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Historial de envíos</h3>
          <p className="text-xs text-gray-500">{logs.length} envío(s) en el rango seleccionado</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex bg-white rounded-lg border border-gray-200 overflow-hidden text-xs">
            {['24h', '7d', '30d'].map(r => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={`px-3 py-1.5 ${range === r ? 'bg-purple-100 text-purple-700 font-semibold' : 'text-gray-600 hover:bg-gray-50'}`}
              >
                {r === '24h' ? 'Últimas 24h' : r === '7d' ? '7 días' : '30 días'}
              </button>
            ))}
          </div>
          <select
            value={campaignFilter}
            onChange={e => setCampaignFilter(e.target.value)}
            className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white"
          >
            <option value="">Todas las campañas</option>
            {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {isAdmin && (
            <label className="flex items-center gap-2 text-xs text-gray-600">
              <input type="checkbox" checked={!onlyMine} onChange={() => setOnlyMine(!onlyMine)} className="w-4 h-4" />
              Ver todas
            </label>
          )}
        </div>
      </div>

      {dayKeys.length === 0 ? (
        <div className="text-center py-12 text-gray-400 bg-white rounded-2xl">
          No hay envíos en este rango.
        </div>
      ) : (
        <div className="space-y-4">
          {dayKeys.map(day => {
            const campaignsInDay = groupedByDayCampaign[day]
            const campaignIds = Object.keys(campaignsInDay).sort((a, b) => campaignsInDay[a].name.localeCompare(campaignsInDay[b].name))
            const totalDay = campaignIds.reduce((s, id) => s + campaignsInDay[id].logs.length, 0)
            return (
              <div key={day} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                  <span className="font-semibold text-sm text-gray-700 capitalize">📅 {formatDayLabel(day)}</span>
                  <span className="text-xs text-gray-500">{totalDay} envío(s) · {campaignIds.length} campaña(s)</span>
                </div>
                {campaignIds.map(cid => {
                  const camp = campaignsInDay[cid]
                  return (
                    <div key={cid} className="border-b border-gray-100 last:border-b-0">
                      <div className="px-4 py-2 bg-purple-50/50 flex items-center gap-2">
                        <span className="text-purple-600">📢</span>
                        <span className="font-medium text-sm text-gray-800 flex-1 truncate">{camp.name}</span>
                        <span className="text-xs text-gray-500">{camp.logs.length} envío(s)</span>
                      </div>
                      <ul className="divide-y divide-gray-50">
                        {camp.logs.map(log => {
                          const avatar = getAvatarData(log.contact_name || log.contact_phone, log.contact_phone)
                          return (
                            <li key={log.id} className="px-4 py-2 flex items-center gap-3 text-sm hover:bg-gray-50">
                              <span className="font-mono text-gray-500 text-xs w-12 shrink-0">{formatTime(log.sent_at)}</span>
                              <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 text-[10px] font-bold w-9 text-center shrink-0">P{log.step_number}</span>
                              <div
                                className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-[11px] shrink-0"
                                style={{ background: avatar.gradient }}
                                title={log.contact_phone}
                              >
                                {avatar.initials}
                              </div>
                              <div className="flex-1 min-w-0 flex items-center gap-2">
                                <span className="text-gray-800 truncate">{log.contact_name || log.contact_phone}</span>
                                <span className="text-gray-400 text-xs shrink-0">{log.contact_phone}</span>
                              </div>
                              <span className="text-gray-500 text-xs italic truncate flex-1 ml-2 hidden md:inline">{log.message_preview}</span>
                              <StatusIcon log={log} />
                            </li>
                          )
                        })}
                      </ul>
                    </div>
                  )
                })}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
